import { logErrorToSentry } from '@modules/common/utils';
import { parseStringifiedDuration } from '@modules/feed/utils/parseStringifiedDuration';
import { decompressFromEncodedURIComponent } from 'lz-string';
import { useCallback } from 'react';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { DashboardActionType } from '../store/dashboardActionType';
import { DashboardQuery } from '../store/types';
import { useDashboardStore } from '../store/useDashboardStore';

export const useInitDashboardStoreFromSearchParams = () => {
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);

  const initDashboardStoreFromSearchParams = useCallback(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const queryPayload: Partial<DashboardQuery> = {};

    // Why not directly utilize populatePayloadFromSearchParams?
    // Current architecture to handle search params is fragile;
    // we shouldn't decode the search params all at once. This can break
    // if we pass additional params which is not decoded.
    // Ideally every parameter should be abstracted and decoded separately.
    // See https://linear.app/enterpret/issue/ENG-11175/better-handle-search-params-in-fe

    // Fetch dashboard query parameters from search params
    const queryParameters = searchParams.get('queryParameters');
    if (queryParameters) {
      const decodedQueryParameters =
        decompressFromEncodedURIComponent(queryParameters);

      const searchParamsToPopulate = new URLSearchParams(
        decodedQueryParameters,
      );
      const queryParamsToPopulate: Partial<
        Record<keyof DashboardQuery, string>
      > = {
        duration: 'dashboardDuration',
        whereClause: 'dashboardWhereClause',
      };

      Object.entries(queryParamsToPopulate).forEach(
        ([payloadKey, searchParamKey]) => {
          const value = searchParamsToPopulate.get(searchParamKey);
          if (value) {
            try {
              queryPayload[payloadKey as keyof DashboardQuery] = JSON.parse(
                value || '',
              );
            } catch (e) {
              logErrorToSentry(
                {
                  errorObj: e,
                  message: 'failed to parse dashboard search param',
                },
                { extra: { value } },
              );
            }
          }
        },
      );
    }

    const queryPayloadToDispatch: Partial<DashboardQuery> = {
      ...queryPayload,
      whereClause: queryPayload.whereClause || EmptyWhereClause,
      duration: queryPayload.duration
        ? parseStringifiedDuration(queryPayload.duration)
        : undefined,
    };

    dispatch({
      type: DashboardActionType.SET_QUERY,
      payload: queryPayloadToDispatch,
    });

    if (queryPayload.whereClause) {
      dispatch({
        type: DashboardActionType.SET_VISUAL,
        payload: {
          shouldShowQueryBuilder: true,
        },
      });
    }
  }, [dispatch]);

  return initDashboardStoreFromSearchParams;
};
